/** Provider formats Spec calls through the AI Gateway, each with the thinking levels it accepts. */
export const SPEC_MODEL_PROVIDERS = {
  openai: { profiles: ['low', 'medium', 'high', 'max'] },
  anthropic: { profiles: ['off', 'low', 'medium', 'high', 'xhigh', 'max'] },
} as const;

export type SpecModelProvider = keyof typeof SPEC_MODEL_PROVIDERS;
export type SpecModelProfile = (typeof SPEC_MODEL_PROVIDERS)[SpecModelProvider]['profiles'][number];

export function isSpecModelProvider(value: string): value is SpecModelProvider {
  return Object.hasOwn(SPEC_MODEL_PROVIDERS, value);
}

/** The one profile rule every gate shares: the row's provider must accept it. */
export function isAcceptedSpecModelProfile(provider: string, profile: string): boolean {
  return (
    isSpecModelProvider(provider) &&
    (SPEC_MODEL_PROVIDERS[provider].profiles as readonly string[]).includes(profile)
  );
}

export type SpecBillingMode = 'byok' | 'credits';

/** How the gateway bills a provider: our own key (the provider bills us) or Cloudflare credits plus a fee. */
export interface SpecProviderBilling {
  mode: SpecBillingMode;
  feePpm: number;
}

const PPM = 1_000_000n;

function ceil(numerator: bigint, denominator: bigint): bigint {
  return numerator === 0n ? 0n : (numerator + denominator - 1n) / denominator;
}

function count(value: number, field: string): bigint {
  if (!Number.isSafeInteger(value) || value < 0) throw new RangeError(`Invalid ${field}.`);
  return BigInt(value);
}

/** Estimates count SPEC_CHARS_PER_TOKEN_ESTIMATE chars per token; a row scales that to its own tokenizer. */
export function toModelTokens(baselineTokens: number, tokenEstimateMultiplierPpm: number): number {
  if (!(baselineTokens > 0)) return 0;
  const multiplier = count(tokenEstimateMultiplierPpm, 'token estimate multiplier');
  return Number(ceil(BigInt(Math.ceil(baselineTokens)) * multiplier, PPM));
}

export interface SpecModelRates {
  inputMicrousdPerMillion: number;
  cachedInputMicrousdPerMillion: number;
  cacheWriteMicrousdPerMillion: number | null;
  outputMicrousdPerMillion: number;
  reasoningMicrousdPerMillion: number;
  longContextThresholdTokens: number | null;
  longContextMultiplierPpm: number;
  landedCostMultiplierPpm: number;
  fixedMicrousdPerRequest: number;
}

export interface SpecCallTokens {
  /** Every input token, cache reads and writes included. */
  inputTokens: number;
  cachedInputTokens: number;
  cacheWriteTokens: number;
  /** Thinking included. */
  outputTokens: number;
  reasoningTokens: number;
}

/** Cloudflare's credit-purchase fee applies to credits-billed providers only. */
export function applyBillingFee(landedMicrousd: number, billing: SpecProviderBilling): number {
  const amount = count(landedMicrousd, 'cost');
  if (billing.mode !== 'credits') return Number(amount);
  return Number(ceil(amount * (PPM + count(billing.feePpm, 'fee')), PPM));
}

/** Landed COGS in integer micro-USD: each token class rounded up, then tier, billing fee, landed multiplier, fixed fee. */
export function priceSpecModelCall(
  rates: SpecModelRates,
  tokens: SpecCallTokens,
  billing: SpecProviderBilling,
): number {
  const input = count(tokens.inputTokens, 'input token count');
  const cached = count(tokens.cachedInputTokens, 'cached input token count');
  const written = count(tokens.cacheWriteTokens, 'cache write token count');
  const output = count(tokens.outputTokens, 'output token count');
  const reasoning = count(tokens.reasoningTokens, 'reasoning token count');
  if (cached + written > input) throw new RangeError('Cache token counts exceed input tokens.');

  const rate = (value: number) => count(value, 'rate');
  const writeRate = rates.cacheWriteMicrousdPerMillion ?? rates.inputMicrousdPerMillion;
  let variable =
    ceil((input - cached - written) * rate(rates.inputMicrousdPerMillion), PPM) +
    ceil(cached * rate(rates.cachedInputMicrousdPerMillion), PPM) +
    ceil(written * rate(writeRate), PPM) +
    ceil(output * rate(rates.outputMicrousdPerMillion), PPM) +
    ceil(reasoning * rate(rates.reasoningMicrousdPerMillion), PPM);
  if (
    rates.longContextThresholdTokens !== null &&
    input > count(rates.longContextThresholdTokens, 'long context threshold')
  ) {
    variable = ceil(variable * rate(rates.longContextMultiplierPpm), PPM);
  }
  if (variable > BigInt(Number.MAX_SAFE_INTEGER))
    throw new RangeError('Calculated cost is too large.');
  const billed = BigInt(applyBillingFee(Number(variable), billing));
  const landed =
    ceil(billed * rate(rates.landedCostMultiplierPpm), PPM) + rate(rates.fixedMicrousdPerRequest);
  if (landed > BigInt(Number.MAX_SAFE_INTEGER))
    throw new RangeError('Calculated cost is too large.');
  return Number(landed);
}
